<section
  id="weather-conditions"
  class="py-5 weather-highlights"
  aria-label="Condições climáticas atuais"
>
  <div class="container">
    <h2 class="mb-4 text-center fw-bold">Condições Climáticas</h2>

    <!-- Seleção de Cidade -->
    <div class="row justify-content-center mb-4">
      <div class="col-12 col-md-6 col-lg-5">
        <form
          class="city-selector"
          role="search"
          aria-label="Selecionar cidade para consulta climática"
          (submit)="onCitySubmit($event)"
        >
          <label for="citySelect" class="visually-hidden"
            >Selecione uma cidade</label
          >
          <div class="input-group">
            <select
              id="citySelect"
              class="form-select"
              required
              [(ngModel)]="selectedCityId"
              name="cityId"
              (change)="onCityChange($event)"
            >
              <option [value]="null" selected>Selecione uma cidade</option>
              @for (city of citiesWithActiveStations; track city.id) {
                <option [value]="city.id">
                  {{ city.name }} - {{ city.state.code }}
                </option>
              }
            </select>
            <button
              class="btn btn-primary px-4"
              type="submit"
              aria-label="Consultar clima"
            >
              <i class="fa-solid fa-magnifying-glass me-2"></i>
              Consultar
            </button>
          </div>
          @if (citySelectionError) {
            <div class="text-danger small mt-2">
              <i class="fa-solid fa-exclamation-circle me-1"></i>
              {{ citySelectionError }}
            </div>
          }
        </form>
      </div>
    </div>

    <!-- Informações da Cidade -->
    @if (!realTimeError || realTimeData) {
      <div class="text-center mb-4">
        <h3 class="h4 fw-bold mb-2" id="cityName">
          <i class="fa-solid fa-location-dot me-2" aria-hidden="true"></i>
          {{ selectedCity }}
          @if (isDetectingLocation) {
            <output
              class="spinner-border spinner-border-sm ms-2"
              aria-label="Detectando localização..."
            ></output>
          }
        </h3>
        @if (realTimeData) {
          <p class="text-muted mb-0">
            <small>
              @if (isManualSelection) {
                Estação ativa e atualizada hoje dia
                {{ realTimeData.generationTime | date: "dd/MM/yyyy" }} às
                {{ (realTimeData.generationTime | date: "HH:mm") ?? "" }}
              } @else {
                Estação ativa e mais próxima de você, atualizada hoje dia
                {{ realTimeData.generationTime | date: "dd/MM/yyyy" }} às
                {{ (realTimeData.generationTime | date: "HH:mm") ?? "" }}
              }
            </small>
          </p>
        } @else if (!realTimeError) {
          <p class="text-muted mb-0">
            <small>
              <i
                class="fa-solid fa-spinner fa-spin me-1"
                aria-hidden="true"
              ></i>
              Carregando dados da estação...
            </small>
          </p>
        }
      </div>
    }

    @if (realTimeError) {
      <!-- Mensagem de erro para dados em tempo real -->
      <div class="row justify-content-center mb-4">
        <div class="col-12 col-md-8 col-lg-6">
          <div class="alert alert-danger text-center" role="alert">
            <i class="fa-solid fa-exclamation-triangle me-2"></i>
            {{ realTimeError }}
          </div>
        </div>
      </div>
    } @else {
      <!-- Cards de clima -->
      <div class="row g-3 g-md-4 justify-content-center">
        <div class="col-6 col-md-4 col-lg">
          <app-weather-card
            icon="fa-thermometer-half"
            [value]="realTimeData ? realTimeData.temp + '°C' : '--'"
            label="Temperatura Atual"
            iconColor="text-warning"
          ></app-weather-card>
        </div>

        <div class="col-6 col-md-4 col-lg">
          <app-weather-card
            icon="fa-temperature-arrow-down"
            [value]="realTimeData ? realTimeData.tempMin + '°C' : '--'"
            label="Temperatura Mínima"
            iconColor="text-info"
          ></app-weather-card>
        </div>

        <div class="col-6 col-md-4 col-lg">
          <app-weather-card
            icon="fa-temperature-arrow-up"
            [value]="realTimeData ? realTimeData.tempMax + '°C' : '--'"
            label="Temperatura Máxima"
            iconColor="text-danger"
          ></app-weather-card>
        </div>

        <div class="col-6 col-md-4 col-lg">
          <app-weather-card
            icon="fa-temperature-half"
            [value]="
              dailyAverageData
                ? dailyAverageData.tempAvg.toFixed(1) + '°C'
                : '--'
            "
            label="Temperatura Média"
            iconColor="text-warning"
          ></app-weather-card>
        </div>

        <div class="col-6 col-md-4 col-lg">
          <app-weather-card
            icon="fa-cloud-rain"
            [value]="realTimeData ? realTimeData.rain + ' mm' : '--'"
            label="Chuva"
            iconColor="text-info"
          ></app-weather-card>
        </div>
      </div>
    }

    <!-- Dados dos Últimos 5 Dias -->
    @if (!realTimeError || lastDaysData.length > 0) {
      <div class="mt-5">
        <h3 class="h4 fw-bold mb-4 text-center">Dados dos Últimos 5 Dias</h3>

        @if (lastDaysError) {
          <!-- Mensagem de erro -->
          <div class="row justify-content-center">
            <div class="col-12 col-md-8 col-lg-6">
              <div class="alert alert-danger text-center" role="alert">
                <i class="fa-solid fa-exclamation-triangle me-2"></i>
                {{ lastDaysError }}
              </div>
            </div>
          </div>
        } @else if (lastDaysData.length > 0) {
          <!-- Cards com dados históricos -->
          <div class="row g-3 justify-content-center">
            @for (dayData of lastDaysData; track dayData.id) {
              <div class="col-lg">
                <app-history-card
                  [date]="
                    (dayData.generationDate | date: 'dd/MM/yyyy - EEEE') ?? ''
                  "
                  [tempMin]="dayData.tempMin"
                  [tempMax]="dayData.tempMax"
                  [rainfall]="dayData.rainAccum"
                ></app-history-card>
              </div>
            }
          </div>
        } @else if (!realTimeError) {
          <!-- Estado de carregamento (só mostra se não houver erro em tempo real) -->
          <div class="text-center text-muted py-4">
            <i class="fa-solid fa-spinner fa-spin me-2"></i>
            Carregando dados históricos...
          </div>
        }
      </div>
    }
  </div>
</section>
